import { Modal, Stack } from '@mui/material'
import { useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import pxToRem from '../../../helpers/pxToRem'
import { useEffect, useState } from 'react'
import { PrimaryButton } from '../../../components/Button'
import styles from '../../../shared/_stylesParams.module.scss'
import {
    GoogleMapsTextField,
    PlaceType,
} from '../../../components/AutoComplete'
import { getErrorMsgForTextInput } from '../../../helpers/inputValidators'
import useTextInput from '../../../hooks/useTextInput'
import { isValidPlaceType } from './helper'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import { setAccountLocation } from '../../../firebase/teacherApi'
import { logger } from '../../../helpers/logger'

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: pxToRem(500),
    height: '90%',
    maxHeight: pxToRem(400),
    backgroundColor: '#fff',
    boxShadow: '0px 4px 4px 0px #00000040',
    borderRadius: pxToRem(16),
}

const HomeAddressModal = () => {
    const userId = useSelector((state: RootState) => state.user.auth.status?.uid)
    const accountType = useSelector(
        (state: RootState) => state.user.user.data?.accountType
    )
    const fields = useSelector((state: RootState) => state.userReg.fields)
    const [loading, setLoading] = useState(false)
    const [errorMsg, setErrorMsg] = useState<string>()

    const homeAddress = useTextInput({
        inputValidator: (input: string) => {
            return getErrorMsgForTextInput(input, 'home address')
        },
        defaultValue: fields.homeAddress?.address || '',
    })
    const [homeLocation, setHomeLocation] = useState<PlaceType | null>(null)
    const handleHomeLocationChange = (place: PlaceType | null) => {
        setHomeLocation(place)
    }

    const [userLocation, setUserLocation] = useState<GeolocationPosition>()
    const [askPermission, setAskPermission] = useState(true)
    const getUserLocation = async () => {
        navigator.geolocation.getCurrentPosition(
            (location) => {
                setUserLocation(location)
                setAskPermission(false)
            },
            (error) => {
                logger.error('permission not given')
                setAskPermission(false)
            }
        )
    }

    const handleSetLocation = async () => {
        setLoading(true)
        setErrorMsg(undefined)
        try {
            if (!userId) {
                throw new Error(
                    'User is not authenticated! Please refresh page and try again.'
                )
            }
            if (!accountType) throw new Error('Invalid account type')
            const locationError = isValidPlaceType(homeLocation)

            if (!homeLocation) {
                throw new Error('Please enter an address')
            }
            if (locationError !== null) {
                throw new Error(locationError)
            }
            await setAccountLocation(userId, accountType, homeLocation)
        } catch (error: any) {
            setErrorMsg(
                error instanceof Error ? error.message : JSON.stringify(error)
            )
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fields.homeAddress?.address
            ? homeAddress.setValue(fields.homeAddress?.address)
            : homeAddress.reset()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.homeAddress])

    useEffect(() => {
        if (
            fields.homeAddress !== undefined &&
            fields.homeAddress.address === undefined
        ) {
            // Ask for location on open
            getUserLocation()
        }
    }, [fields.homeAddress, fields.homeAddress?.address])

    useEffect(() => {
        if (fields.setLocation?.homeState) {
            localStorage.setItem('homeState', fields.setLocation?.homeState)
        } else if (fields.homeAddress?.homeState) {
            localStorage.setItem('homeState', fields.homeAddress?.homeState)
        }
    }, [fields.setLocation?.homeState, fields.homeAddress?.homeState])

    return (
        <>
            <Modal
                open={
                    fields.homeAddress !== undefined &&
                    fields.homeAddress.address === undefined
                }
            >
                <Stack
                    style={{
                        ...modalStyle,
                        height: 'fit-content',
                        maxHeight: '90%',
                    }}
                    sx={{ padding: pxToRem(40), gap: pxToRem(20) }}
                >
                    <h3>Find Your Home Address</h3>
                    {errorMsg && (
                        <p style={{ color: styles.destructiveRed500 }}>
                            {errorMsg}
                        </p>
                    )}
                    {/* <CountryInput value={country} setValue={(e)=>setCountry(e)}/>
                    <StateInput value={homeState} setValue={(e) => setHomeState(e)} country={country} disabled={!country || (stateListCount!==undefined && stateListCount === 0)} stateCount={(count: number) => setStateListCount(count)}/> */}
                    {askPermission ? (
                        <Stack height="100%" width="100%">
                            <LoadingSpinner />
                        </Stack>
                    ) : (
                        <>
                            <GoogleMapsTextField
                                inputValue={homeAddress.value}
                                setInputValue={homeAddress.setValue}
                                value={null}
                                setValue={handleHomeLocationChange}
                                label="Home Address"
                                location={
                                    userLocation
                                        ? {
                                              lat: userLocation.coords.latitude,
                                              lng: userLocation.coords
                                                  .longitude,
                                          }
                                        : undefined
                                }
                            />
                            <PrimaryButton
                                onClick={handleSetLocation}
                                sx={{ marginTop: pxToRem(40) }}
                                disabled={!homeLocation}
                                loading={loading}
                            >
                                Get started
                            </PrimaryButton>
                        </>
                    )}
                </Stack>
            </Modal>
        </>
    )
}

export default HomeAddressModal
